// src/components/BucksAdminReports.jsx

import React, { useState, useEffect, useRef } from 'react';
import '../stylings/BucksAdminReports.css';
// Import Chart.js
import Chart from 'chart.js/auto';

// Define API endpoints (replace with actual endpoints when available)
const GET_SALES_REPORT_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-sales-report-get';
const GET_INVENTORY_REPORT_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-inventory-report-get';
const GET_CUSTOMER_REPORT_URL = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-customer-report-get';

function BucksAdminReports() {
  /* ────────────────────── STATE ────────────────────── */
  // Report data
  const [salesData, setSalesData] = useState(null);
  const [inventoryData, setInventoryData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [topProductsData, setTopProductsData] = useState(null);
  const [categoryData, setCategoryData] = useState(null);
  
  // Filter states
  const [reportPeriod, setReportPeriod] = useState('last30days');
  const [reportCategory, setReportCategory] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({
    from: '',
    to: ''
  });
  
  // UI states
  const [loading, setLoading] = useState({
    sales: false,
    inventory: false,
    customer: false
  });
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [chartType, setChartType] = useState({
    sales: 'line',
    inventory: 'bar',
    category: 'pie',
    customers: 'line'
  });
  
  // Chart refs for rendering
  const salesChartRef = useRef(null);
  const inventoryChartRef = useRef(null);
  const categoryChartRef = useRef(null);
  const customerChartRef = useRef(null);
  
  // Chart instances (for cleanup)
  const [chartInstances, setChartInstances] = useState({
    sales: null,
    inventory: null,
    category: null,
    customers: null
  });
  
  /* ────────────────────── EFFECTS ────────────────────── */
  // Fetch reports when component mounts or filters change
  useEffect(() => {
    fetchReports();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Render charts when data is available
  useEffect(() => {
    if (salesData) {
      renderSalesChart();
    }
    if (inventoryData) {
      renderInventoryChart();
    }
    if (categoryData) {
      renderCategoryChart();
    }
    if (customerData) {
      renderCustomerChart();
    }
  }, [salesData, inventoryData, categoryData, customerData, chartType]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Cleanup charts on unmount
  useEffect(() => {
    return () => {
      // Destroy all chart instances when component unmounts
      Object.values(chartInstances).forEach(chart => {
        if (chart) chart.destroy();
      });
    };
  }, [chartInstances]);
  
  /* ────────────────────── API CALLS ────────────────────── */
  // Fetch all report data
  const fetchReports = async () => {
    setLoading({
      sales: true,
      inventory: true,
      customer: true
    });
    
    try {
      // Convert filter options to API params
      const dateParams = getDateParamsFromPeriod();
      
      // For demo purposes, we'll use mock data instead of calling actual APIs
      // In production, uncomment the fetch calls below
      
      /*
      // Fetch sales report
      const salesResponse = await fetch(`${GET_SALES_REPORT_URL}?${new URLSearchParams({
        ...dateParams,
        category: reportCategory
      })}`);
      
      if (!salesResponse.ok) {
        throw new Error(`Sales API request failed with status ${salesResponse.status}`);
      }
      
      const salesResult = await salesResponse.json();
      setSalesData(salesResult.salesData);
      setTopProductsData(salesResult.topProducts);
      setCategoryData(salesResult.categoryData);
      
      // Fetch inventory report
      const inventoryResponse = await fetch(`${GET_INVENTORY_REPORT_URL}?${new URLSearchParams({
        ...dateParams,
        category: reportCategory
      })}`);
      
      if (!inventoryResponse.ok) {
        throw new Error(`Inventory API request failed with status ${inventoryResponse.status}`);
      }
      
      const inventoryResult = await inventoryResponse.json();
      setInventoryData(inventoryResult.inventoryData);
      
      // Fetch customer report
      const customerResponse = await fetch(`${GET_CUSTOMER_REPORT_URL}?${new URLSearchParams({
        ...dateParams
      })}`);
      
      if (!customerResponse.ok) {
        throw new Error(`Customer API request failed with status ${customerResponse.status}`);
      }
      
      const customerResult = await customerResponse.json();
      setCustomerData(customerResult.customerData);
      */
      
      // Mock data for development
      const mockData = generateMockData(dateParams.period);
      
      // Set mock data with some delay to simulate API calls
      setTimeout(() => {
        setSalesData(mockData.salesData);
        setTopProductsData(mockData.topProducts);
        setCategoryData(mockData.categoryData);
        setInventoryData(mockData.inventoryData);
        setCustomerData(mockData.customerData);
        
        setLoading({
          sales: false,
          inventory: false,
          customer: false
        });
      }, 800);
      
    } catch (error) {
      console.error('Error fetching reports:', error);
      setNotification({
        show: true,
        message: `Error loading reports: ${error.message}`,
        type: 'error'
      });
      
      setLoading({
        sales: false,
        inventory: false,
        customer: false
      });
    }
  };
  
  /* ────────────────────── CHART RENDERING ────────────────────── */
  // Render sales chart
  const renderSalesChart = () => {
    if (!salesChartRef.current || !salesData) return;
    
    const ctx = salesChartRef.current.getContext('2d');
    
    // Clear previous chart
    if (chartInstances.sales) {
      chartInstances.sales.destroy();
    }
    
    // Prepare data for chart
    const labels = salesData.map(item => item.date);
    const revenue = salesData.map(item => item.revenue);
    const orders = salesData.map(item => item.orders);
    
    // Create chart
    const newChart = new Chart(ctx, {
      type: chartType.sales,
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Revenue',
            data: revenue,
            borderColor: 'rgba(166, 124, 82, 1)',
            backgroundColor: 'rgba(166, 124, 82, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            yAxisID: 'y'
          },
          {
            label: 'Orders',
            data: orders,
            borderColor: 'rgba(59, 130, 246, 1)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderWidth: 2,
            tension: 0.4,
            yAxisID: 'y1'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Revenue ($)'
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              drawOnChartArea: false
            },
            title: {
              display: true,
              text: 'Orders'
            }
          }
        }
      }
    });
    
    // Update chart instances
    setChartInstances(prev => ({
      ...prev,
      sales: newChart
    }));
  };
  
  // Render inventory chart
  const renderInventoryChart = () => {
    if (!inventoryChartRef.current || !inventoryData) return;
    
    const ctx = inventoryChartRef.current.getContext('2d');
    
    // Clear previous chart
    if (chartInstances.inventory) {
      chartInstances.inventory.destroy();
    }
    
    // Prepare data for chart
    const labels = inventoryData.map(item => item.category);
    const stock = inventoryData.map(item => item.stock);
    const value = inventoryData.map(item => item.value);
    
    // Create chart
    const newChart = new Chart(ctx, {
      type: chartType.inventory,
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Stock',
            data: stock,
            backgroundColor: 'rgba(166, 124, 82, 0.7)',
            yAxisID: 'y'
          },
          {
            label: 'Value',
            data: value,
            backgroundColor: 'rgba(139, 92, 246, 0.7)',
            yAxisID: 'y1'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            title: {
              display: true,
              text: 'Units'
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              drawOnChartArea: false
            },
            title: {
              display: true,
              text: 'Value ($)'
            }
          }
        }
      }
    });
    
    // Update chart instances
    setChartInstances(prev => ({
      ...prev,
      inventory: newChart
    }));
  };
  
  // Render category chart
  const renderCategoryChart = () => {
    if (!categoryChartRef.current || !categoryData) return;
    
    const ctx = categoryChartRef.current.getContext('2d');
    
    // Clear previous chart
    if (chartInstances.category) {
      chartInstances.category.destroy();
    }
    
    // Prepare data for chart
    const labels = categoryData.map(item => item.category);
    const sales = categoryData.map(item => item.sales);
    
    // Chart colors
    const backgroundColors = [
      'rgba(166, 124, 82, 0.7)',
      'rgba(139, 92, 246, 0.7)',
      'rgba(16, 185, 129, 0.7)',
      'rgba(245, 158, 11, 0.7)',
      'rgba(59, 130, 246, 0.7)',
      'rgba(239, 68, 68, 0.7)',
      'rgba(107, 114, 128, 0.7)'
    ];
    
    // Create chart
    const newChart = new Chart(ctx, {
      type: chartType.category,
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Sales by Category',
            data: sales,
            backgroundColor: backgroundColors
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
    
    // Update chart instances
    setChartInstances(prev => ({
      ...prev,
      category: newChart
    }));
  };
  
  // Render customer chart
  const renderCustomerChart = () => {
    if (!customerChartRef.current || !customerData) return;
    
    const ctx = customerChartRef.current.getContext('2d');
    
    // Clear previous chart
    if (chartInstances.customers) {
      chartInstances.customers.destroy();
    }
    
    // Prepare data for chart
    const labels = customerData.map(item => item.date);
    const newCustomers = customerData.map(item => item.newCustomers);
    const returningCustomers = customerData.map(item => item.returningCustomers);
    
    // Create chart
    const newChart = new Chart(ctx, {
      type: chartType.customers,
      data: {
        labels: labels,
        datasets: [
          {
            label: 'New Customers',
            data: newCustomers,
            borderColor: 'rgba(16, 185, 129, 1)',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderWidth: 2,
            tension: 0.4
          },
          {
            label: 'Returning Customers',
            data: returningCustomers,
            borderColor: 'rgba(245, 158, 11, 1)',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderWidth: 2,
            tension: 0.4
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
    
    // Update chart instances
    setChartInstances(prev => ({
      ...prev,
      customers: newChart
    }));
  };
  
  /* ────────────────────── EVENT HANDLERS ────────────────────── */
  // Handle period selection change
  const handlePeriodChange = (e) => {
    setReportPeriod(e.target.value);
    
    // If custom period, don't fetch reports yet
    if (e.target.value !== 'custom') {
      // Reset custom date range
      setCustomDateRange({ from: '', to: '' });
    }
  };
  
  // Handle category filter change
  const handleCategoryChange = (e) => {
    setReportCategory(e.target.value);
  };
  
  // Handle custom date range change
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setCustomDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Handle generate report button click
  const handleGenerateReport = () => {
    // Validate custom date range if needed
    if (reportPeriod === 'custom' && (!customDateRange.from || !customDateRange.to)) {
      setNotification({
        show: true,
        message: 'Please select both start and end dates for custom date range.',
        type: 'warning'
      });
      return;
    }
    
    // Fetch reports
    fetchReports();
  };
  
  // Handle reset filters button click
  const handleResetFilters = () => {
    setReportPeriod('last30days');
    setReportCategory('all');
    setCustomDateRange({ from: '', to: '' });
  };
  
  // Handle chart type change
  const handleChartTypeChange = (chart, type) => {
    setChartType(prev => ({
      ...prev,
      [chart]: type
    }));
  };
  
  // Handle export data button click
  const handleExportData = (dataType) => {
    // In production, this would trigger an API call to get the data in CSV format
    // For demo, show a notification
    setNotification({
      show: true,
      message: `${dataType} data exported successfully!`,
      type: 'success'
    });
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };
  
  /* ────────────────────── UTILITY FUNCTIONS ────────────────────── */
  // Get date parameters based on selected period
  const getDateParamsFromPeriod = () => {
    const today = new Date();
    let fromDate, toDate;
    
    switch (reportPeriod) {
      case 'today':
        fromDate = new Date(today);
        toDate = new Date(today);
        break;
      case 'yesterday':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 1);
        toDate = new Date(fromDate);
        break;
      case 'last7days':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 7);
        toDate = new Date(today);
        break;
      case 'last30days':
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 30);
        toDate = new Date(today);
        break;
      case 'thisMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        toDate = new Date(today);
        break;
      case 'lastMonth':
        fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        toDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'thisYear':
        fromDate = new Date(today.getFullYear(), 0, 1);
        toDate = new Date(today);
        break;
      case 'custom':
        fromDate = customDateRange.from ? new Date(customDateRange.from) : null;
        toDate = customDateRange.to ? new Date(customDateRange.to) : null;
        break;
      default:
        fromDate = new Date(today);
        fromDate.setDate(fromDate.getDate() - 30);
        toDate = new Date(today);
    }
    
    return {
      period: reportPeriod,
      from_date: fromDate ? fromDate.toISOString().split('T')[0] : null,
      to_date: toDate ? toDate.toISOString().split('T')[0] : null
    };
  };
  
  // Format currency
  const formatCurrency = (value) => {
    return `$${parseFloat(value).toFixed(2)}`;
  };
  
  // Format percentage
  const formatPercentage = (value) => {
    return `${parseFloat(value).toFixed(1)}%`;
  };
  
  // Get trend class
  const getTrendClass = (value) => {
    if (value > 0) return 'up';
    if (value < 0) return 'down';
    return 'neutral';
  };
  
  // Generate mock data for development
  const generateMockData = (period) => {
    // Generate dates based on period
    const dates = generateDatesForPeriod(period);
    
    // Mock sales data
    const salesData = dates.map(date => ({
      date: date,
      revenue: Math.floor(Math.random() * 5000) + 500,
      orders: Math.floor(Math.random() * 50) + 5
    }));
    
    // Calculate sales totals
    const totalRevenue = salesData.reduce((sum, item) => sum + item.revenue, 0);
    const totalOrders = salesData.reduce((sum, item) => sum + item.orders, 0);
    const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    
    // Mock top products
    const topProducts = [
      { id: 'p001', name: 'Organic Apples', quantity: 120, revenue: 599.80 },
      { id: 'p002', name: 'Farm Fresh Eggs', quantity: 85, revenue: 509.15 },
      { id: 'p003', name: 'Strawberry Jam', quantity: 64, revenue: 447.36 },
      { id: 'p004', name: 'Honey', quantity: 52, revenue: 467.48 },
      { id: 'p005', name: 'Sourdough Bread', quantity: 45, revenue: 359.55 }
    ];
    
    // Mock category data
    const categoryData = [
      { category: 'Fruit', sales: Math.floor(Math.random() * 2000) + 1000 },
      { category: 'Vegetable', sales: Math.floor(Math.random() * 1800) + 800 },
      { category: 'Bakery', sales: Math.floor(Math.random() * 1200) + 600 },
      { category: 'Dairy', sales: Math.floor(Math.random() * 1500) + 700 },
      { category: 'Preserves', sales: Math.floor(Math.random() * 1000) + 500 }
    ];
    
    // Mock inventory data
    const inventoryData = [
      { category: 'Fruit', stock: 250, value: 1250 },
      { category: 'Vegetable', stock: 180, value: 900 },
      { category: 'Bakery', stock: 120, value: 960 },
      { category: 'Dairy', stock: 150, value: 1050 },
      { category: 'Preserves', stock: 100, value: 900 }
    ];
    
    // Mock customer data
    const customerData = dates.map(date => ({
      date: date,
      newCustomers: Math.floor(Math.random() * 10) + 1,
      returningCustomers: Math.floor(Math.random() * 20) + 5
    }));
    
    // Calculate customer totals
    const totalNewCustomers = customerData.reduce((sum, item) => sum + item.newCustomers, 0);
    const totalReturningCustomers = customerData.reduce((sum, item) => sum + item.returningCustomers, 0);
    const customerRetentionRate = Math.round((totalReturningCustomers / (totalNewCustomers + totalReturningCustomers)) * 100);
    
    return {
      salesData,
      topProducts,
      categoryData,
      inventoryData,
      customerData,
      summaryData: {
        totalRevenue,
        totalOrders,
        averageOrderValue,
        totalNewCustomers,
        totalReturningCustomers,
        customerRetentionRate
      }
    };
  };
  
  // Generate dates for period
  const generateDatesForPeriod = (period) => {
    const dates = [];
    const today = new Date();
    let numDays = 30; // Default
    
    switch (period) {
      case 'today':
        return [formatDateString(today)];
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return [formatDateString(yesterday)];
      case 'last7days':
        numDays = 7;
        break;
      case 'last30days':
        numDays = 30;
        break;
      case 'thisMonth':
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        numDays = Math.ceil((today - firstDay) / (1000 * 60 * 60 * 24)) + 1;
        break;
      case 'lastMonth':
        numDays = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
        break;
      case 'thisYear':
        // Just use monthly data for this year
        for (let month = 0; month <= today.getMonth(); month++) {
          dates.push(formatDateString(new Date(today.getFullYear(), month, 15)));
        }
        return dates;
      case 'custom':
        // For demo, just return 14 days
        numDays = 14;
        break;
      default:
        numDays = 30;
    }
    
    // Generate dates
    for (let i = numDays - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      dates.push(formatDateString(date));
    }
    
    return dates;
  };
  
  // Format date as string
  const formatDateString = (date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  /* ────────────────────── RENDER ────────────────────── */
  return (
    <div className="bucks-admin-reports-section">
      {/* Notification */}
      {notification.show && (
        <div className={`bucks-admin-reports-notification ${notification.type}`}>
          <span className="bucks-admin-reports-notification-message">{notification.message}</span>
          <button 
            className="bucks-admin-reports-close-notification" 
            onClick={() => setNotification({ show: false, message: '', type: '' })}
          >
            ×
          </button>
        </div>
      )}
      
      {/* Report Controls */}
      <div className="bucks-admin-reports-controls">
        <div className="bucks-admin-reports-control-group">
          <label htmlFor="report-period" className="bucks-admin-reports-control-label">Report Period:</label>
          <select
            id="report-period"
            value={reportPeriod}
            onChange={handlePeriodChange}
            className="bucks-admin-reports-period-select"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7days">Last 7 Days</option>
            <option value="last30days">Last 30 Days</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="thisYear">This Year</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
        
        {reportPeriod === 'custom' && (
          <div className="bucks-admin-reports-control-group">
            <label className="bucks-admin-reports-control-label">Custom Date Range:</label>
            <div className="bucks-admin-reports-date-inputs">
              <input
                type="date"
                name="from"
                value={customDateRange.from}
                onChange={handleDateChange}
                className="bucks-admin-reports-date-input"
                placeholder="From"
              />
              <span className="bucks-admin-reports-date-separator">to</span>
              <input
                type="date"
                name="to"
                value={customDateRange.to}
                onChange={handleDateChange}
                className="bucks-admin-reports-date-input"
                placeholder="To"
              />
            </div>
          </div>
        )}
        
        <div className="bucks-admin-reports-control-group">
          <label htmlFor="report-category" className="bucks-admin-reports-control-label">Product Category:</label>
          <select
            id="report-category"
            value={reportCategory}
            onChange={handleCategoryChange}
            className="bucks-admin-reports-category-select"
          >
            <option value="all">All Categories</option>
            <option value="fruit">Fruit</option>
            <option value="vegetable">Vegetable</option>
            <option value="bakery">Bakery</option>
            <option value="dairy">Dairy</option>
            <option value="preserves">Preserves</option>
          </select>
        </div>
        
        <div className="bucks-admin-reports-control-group">
          <button 
            className="bucks-admin-reports-reset-btn"
            onClick={handleResetFilters}
          >
            Reset Filters
          </button>
          <button 
            className="bucks-admin-reports-generate-btn"
            onClick={handleGenerateReport}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7"></path>
              <line x1="16" y1="5" x2="22" y2="5"></line>
              <line x1="19" y1="2" x2="19" y2="8"></line>
              <circle cx="9" cy="9" r="2"></circle>
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
            </svg>
            Generate Report
          </button>
        </div>
      </div>
      
      {/* Loading State */}
      {(loading.sales || loading.inventory || loading.customer) && (
        <div className="bucks-admin-reports-loading">
          <div className="bucks-admin-reports-loading-spinner"></div>
          <span>Generating reports...</span>
        </div>
      )}
      
      {/* Report Content - Only show if data is available */}
      {salesData && !loading.sales && (
        <>
          {/* Summary Cards */}
          <div className="bucks-admin-reports-summary">
            <div className="bucks-admin-reports-summary-card">
              <div className="bucks-admin-reports-summary-title">Total Revenue</div>
              <div className="bucks-admin-reports-summary-value">
                {formatCurrency(salesData.reduce((sum, item) => sum + item.revenue, 0))}
              </div>
              <div className={`bucks-admin-reports-summary-trend ${getTrendClass(8.5)}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
                <span>8.5% from previous period</span>
              </div>
            </div>
            
            <div className="bucks-admin-reports-summary-card">
              <div className="bucks-admin-reports-summary-title">Total Orders</div>
              <div className="bucks-admin-reports-summary-value">
                {salesData.reduce((sum, item) => sum + item.orders, 0)}
              </div>
              <div className={`bucks-admin-reports-summary-trend ${getTrendClass(12.3)}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
                <span>12.3% from previous period</span>
              </div>
            </div>
            
            <div className="bucks-admin-reports-summary-card">
              <div className="bucks-admin-reports-summary-title">Average Order Value</div>
              <div className="bucks-admin-reports-summary-value">
                {formatCurrency(salesData.reduce((sum, item) => sum + item.revenue, 0) / 
                 salesData.reduce((sum, item) => sum + item.orders, 0))}
              </div>
              <div className={`bucks-admin-reports-summary-trend ${getTrendClass(-3.2)}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
                  <polyline points="17 18 23 18 23 12"></polyline>
                </svg>
                <span>3.2% from previous period</span>
              </div>
            </div>
            
            <div className="bucks-admin-reports-summary-card">
              <div className="bucks-admin-reports-summary-title">Customer Retention</div>
              <div className="bucks-admin-reports-summary-value">
                {customerData && formatPercentage(customerData.reduce((sum, item) => sum + item.returningCustomers, 0) / 
                 (customerData.reduce((sum, item) => sum + item.newCustomers, 0) + 
                  customerData.reduce((sum, item) => sum + item.returningCustomers, 0)) * 100)}
              </div>
              <div className={`bucks-admin-reports-summary-trend ${getTrendClass(5.7)}`}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
                  <polyline points="17 6 23 6 23 12"></polyline>
                </svg>
                <span>5.7% from previous period</span>
              </div>
            </div>
          </div>
          
          {/* Sales Chart */}
          <div className="bucks-admin-reports-chart-section">
            <div className="bucks-admin-reports-chart-header">
              <h3 className="bucks-admin-reports-chart-title">Sales Performance</h3>
              <div className="bucks-admin-reports-chart-options">
                <button 
                  className={`bucks-admin-reports-chart-option ${chartType.sales === 'line' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('sales', 'line')}
                  title="Line Chart"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                  </svg>
                </button>
                <button 
                  className={`bucks-admin-reports-chart-option ${chartType.sales === 'bar' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('sales', 'bar')}
                  title="Bar Chart"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10"></line>
                    <line x1="12" y1="20" x2="12" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="14"></line>
                  </svg>
                </button>
                <button 
                  className="bucks-admin-reports-export-btn"
                  onClick={() => handleExportData('Sales')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Export Data
                </button>
              </div>
            </div>
            <div className="bucks-admin-reports-chart-content">
              <canvas ref={salesChartRef}></canvas>
            </div>
            <div className="bucks-admin-reports-chart-legend">
              <div className="bucks-admin-reports-legend-item">
                <div className="bucks-admin-reports-legend-color" style={{ backgroundColor: 'rgba(166, 124, 82, 1)' }}></div>
                <span className="bucks-admin-reports-legend-label">Revenue</span>
              </div>
              <div className="bucks-admin-reports-legend-item">
                <div className="bucks-admin-reports-legend-color" style={{ backgroundColor: 'rgba(59, 130, 246, 1)' }}></div>
                <span className="bucks-admin-reports-legend-label">Orders</span>
              </div>
            </div>
          </div>
          
          {/* Two Column Charts */}
          <div className="bucks-admin-reports-columns">
            {/* Category Sales Chart */}
            <div className="bucks-admin-reports-chart-section">
              <div className="bucks-admin-reports-chart-header">
                <h3 className="bucks-admin-reports-chart-title">Sales by Category</h3>
                <div className="bucks-admin-reports-chart-options">
                  <button 
                    className={`bucks-admin-reports-chart-option ${chartType.category === 'pie' ? 'active' : ''}`}
                    onClick={() => handleChartTypeChange('category', 'pie')}
                    title="Pie Chart"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.21 15.89A10 10 0 1 1 8 2.83"></path>
                      <path d="M22 12A10 10 0 0 0 12 2v10z"></path>
                    </svg>
                  </button>
                  <button 
                    className={`bucks-admin-reports-chart-option ${chartType.category === 'doughnut' ? 'active' : ''}`}
                    onClick={() => handleChartTypeChange('category', 'doughnut')}
                    title="Doughnut Chart"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="bucks-admin-reports-chart-content bucks-admin-reports-half-chart">
                <canvas ref={categoryChartRef}></canvas>
              </div>
            </div>
            
            {/* Customer Chart */}
            <div className="bucks-admin-reports-chart-section">
              <div className="bucks-admin-reports-chart-header">
                <h3 className="bucks-admin-reports-chart-title">Customer Activity</h3>
                <div className="bucks-admin-reports-chart-options">
                  <button 
                    className={`bucks-admin-reports-chart-option ${chartType.customers === 'line' ? 'active' : ''}`}
                    onClick={() => handleChartTypeChange('customers', 'line')}
                    title="Line Chart"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
                    </svg>
                  </button>
                  <button 
                    className={`bucks-admin-reports-chart-option ${chartType.customers === 'bar' ? 'active' : ''}`}
                    onClick={() => handleChartTypeChange('customers', 'bar')}
                    title="Bar Chart"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="20" x2="18" y2="10"></line>
                      <line x1="12" y1="20" x2="12" y2="4"></line>
                      <line x1="6" y1="20" x2="6" y2="14"></line>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="bucks-admin-reports-chart-content bucks-admin-reports-half-chart">
                <canvas ref={customerChartRef}></canvas>
              </div>
            </div>
          </div>
          
          {/* Inventory Chart */}
          <div className="bucks-admin-reports-chart-section">
            <div className="bucks-admin-reports-chart-header">
              <h3 className="bucks-admin-reports-chart-title">Inventory Overview</h3>
              <div className="bucks-admin-reports-chart-options">
                <button 
                  className={`bucks-admin-reports-chart-option ${chartType.inventory === 'bar' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('inventory', 'bar')}
                  title="Bar Chart"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10"></line>
                    <line x1="12" y1="20" x2="12" y2="4"></line>
                    <line x1="6" y1="20" x2="6" y2="14"></line>
                  </svg>
                </button>
                <button 
                  className={`bucks-admin-reports-chart-option ${chartType.inventory === 'polarArea' ? 'active' : ''}`}
                  onClick={() => handleChartTypeChange('inventory', 'polarArea')}
                  title="Polar Area Chart"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M13 22H9a7 7 0 1 1 8-8 4 4 0 1 1-4 8Z"></path>
                  </svg>
                </button>
                <button 
                  className="bucks-admin-reports-export-btn"
                  onClick={() => handleExportData('Inventory')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Export Data
                </button>
              </div>
            </div>
            <div className="bucks-admin-reports-chart-content">
              <canvas ref={inventoryChartRef}></canvas>
            </div>
            <div className="bucks-admin-reports-chart-legend">
              <div className="bucks-admin-reports-legend-item">
                <div className="bucks-admin-reports-legend-color" style={{ backgroundColor: 'rgba(166, 124, 82, 0.7)' }}></div>
                <span className="bucks-admin-reports-legend-label">Stock (Units)</span>
              </div>
              <div className="bucks-admin-reports-legend-item">
                <div className="bucks-admin-reports-legend-color" style={{ backgroundColor: 'rgba(139, 92, 246, 0.7)' }}></div>
                <span className="bucks-admin-reports-legend-label">Value ($)</span>
              </div>
            </div>
          </div>
          
          {/* Top Products Table */}
          <div className="bucks-admin-reports-table-section">
            <div className="bucks-admin-reports-table-header">
              <h3 className="bucks-admin-reports-table-title">Top Selling Products</h3>
              <button 
                className="bucks-admin-reports-export-btn"
                onClick={() => handleExportData('Top Products')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export CSV
              </button>
            </div>
            <div className="bucks-admin-reports-table-container">
              <table className="bucks-admin-reports-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Product ID</th>
                    <th>Quantity Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topProductsData && topProductsData.map(product => (
                    <tr key={product.id}>
                      <td className="highlight">{product.name}</td>
                      <td>{product.id}</td>
                      <td>{product.quantity}</td>
                      <td>{formatCurrency(product.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      
      {/* If no data available and not loading, show message */}
      {!salesData && !loading.sales && !loading.inventory && !loading.customer && (
        <div className="bucks-admin-reports-loading">
          <p>Select a report period and click "Generate Report" to view data.</p>
        </div>
      )}
    </div>
  );
}

export default BucksAdminReports;